import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
// Utility for tailwind class merging
function cn(...inputs: (string | undefined | null | false)[]) {
  return twMerge(clsx(inputs));
}
export interface Tab {
  id: string;
  label: string;
  image: string;
  title: string;
  description: string;
  badges?: {
    text: string;
    color: 'teal' | 'blue' | 'purple' | 'green';
  }[];
}
export interface TabbedShowcaseProps {
  tabs: Tab[];
  sectionTitle?: string;
  sectionDescription?: string;
  accentColor?: 'teal' | 'blue';
}
const badgeColors = {
  teal: 'bg-[#ccefff] text-[#004455]',
  blue: 'bg-[#dbeafe] text-[#1e40af]',
  purple: 'bg-[#f3e8ff] text-[#6b21a8]',
  green: 'bg-[#dcfce7] text-[#166534]'
};
export function TabbedShowcase({
  tabs,
  sectionTitle,
  sectionDescription,
  accentColor = 'teal'
}: TabbedShowcaseProps) {
  const [activeTabId, setActiveTabId] = useState(tabs[0].id);
  const activeTab = tabs.find(tab => tab.id === activeTabId) || tabs[0];
  const activeColorClass = accentColor === 'teal' ? 'text-[#0088a9]' : 'text-[#2563eb]';
  const activeBgClass = accentColor === 'teal' ? 'bg-[#0088a9]' : 'bg-[#2563eb]';
  return <section className="w-full max-w-[1728px] mx-auto bg-[#f5f8fc] py-20 px-6 md:px-0" data-id="element-122">
      <div className="max-w-[1200px] mx-auto flex flex-col items-center" data-id="element-123">
        {/* Section Header */}
        {(sectionTitle || sectionDescription) && <div className="text-center mb-12 max-w-3xl" data-id="element-124">
            {sectionTitle && <h2 className="text-[30.6px] font-bold text-gray-900 mb-4" data-id="element-125">
                {sectionTitle}
              </h2>}
            {sectionDescription && <p className="text-[17px] text-gray-600" data-id="element-126">{sectionDescription}</p>}
          </div>}

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12 w-full max-w-[980px]" data-id="element-127">
          <div className="inline-flex p-1 bg-white/50 backdrop-blur-sm rounded-full border border-gray-200/50 shadow-sm overflow-x-auto max-w-full no-scrollbar" data-id="element-128">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className={cn('relative px-6 py-2.5 rounded-full text-[15px] font-medium transition-colors duration-200 whitespace-nowrap', activeTabId === tab.id ? 'text-white' : 'text-gray-600 hover:text-gray-900')} data-id="element-129">
                {activeTabId === tab.id && <motion.div layoutId="activeTabIndicator" className={cn('absolute inset-0 rounded-full shadow-sm', activeBgClass)} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-130" />}
                <span className="relative z-10" data-id="element-131">{tab.label}</span>
              </button>)}
          </div>
        </div>

        {/* Content Area */}
        <div className="w-full max-w-[980px] relative" data-id="element-132">
          <AnimatePresence mode="wait" data-id="element-133">
            <motion.div key={activeTab.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="bg-white rounded-2xl shadow-lg p-8 w-full overflow-hidden border border-gray-100/50" data-id="element-134">
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center" data-id="element-135">
                {/* Text Content */}
                <div className="w-full md:w-1/2 pt-2 order-2 md:order-1" data-id="element-136">
                  <h3 className="text-[24px] font-bold text-gray-900 mb-4" data-id="element-137">
                    {activeTab.title}
                  </h3>

                  <p className="text-[17px] text-gray-600 mb-8 leading-relaxed" data-id="element-138">
                    {activeTab.description}
                  </p>

                  {activeTab.badges && activeTab.badges.length > 0 && <div className="flex flex-wrap gap-3" data-id="element-139">
                      {activeTab.badges.map((badge, idx) => <motion.span key={`${activeTab.id}-badge-${idx}`} initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.1 + idx * 0.05
                  }} className={cn('px-3 py-1 rounded-full text-[12px] font-medium', badgeColors[badge.color] || badgeColors.teal)} data-id="element-140">
                          {badge.text}
                        </motion.span>)}
                    </div>}
                </div>

                {/* Image Content */}
                <div className="w-full md:w-1/2 relative order-1 md:order-2" data-id="element-141">
                  <div className="relative rounded-lg overflow-hidden shadow-md bg-gray-50 border border-gray-100" data-id="element-142">
                    <div className="w-full relative" data-id="element-143">
                      <img src={activeTab.image} alt={activeTab.title} className="w-full h-auto object-contain" data-id="element-144" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}